<script lang="ts">
    import tabIcon from "@/assets/narvarostatistik.svg";

    export default {
        name: "FormPlats",
        tabName: "Plats",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import { computed, ref, toRefs } from "vue";
    import emitter from "tiny-emitter/instance";
    import { AxiosError } from "axios";
    import { useStore } from "@/store";
    import { flashMessage, flashWarning } from "@/utils";
    import { useKForm } from "@/composables/useKForm";
    import KForm from "@/components/forms/Form/KForm.vue";
    import KHeader from "@/components/KHeader.vue";
    import KText from "@/components/forms/inputs/Text/KText.vue";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
        parameters?: {
            id: string;
            namn: string;
            underlag: string;
        };
    }>();

    const { parameters, tabId } = toRefs(props);

    const { inputErrors, formErrors, handleErrorResponse } = useKForm();

    const formRef = ref();

    const isEditing = computed(() => !!parameters?.value?.id);

    const heading = computed(() => (isEditing.value ? "Ändra på plats" : "Skapa ny plats"));

    const submitLabel = computed(() => (isEditing.value ? "Uppdatera plats" : "Skapa plats"));

    const hasUnsavedData = computed(() => formRef.value?.hasUnsavedData?.());

    defineExpose({ hasUnsavedData, tabId });

    const initForm = () => {
        const plats = { ...parameters?.value } as Record<string, any>;
        return {
            ...plats,
            underlag: plats.underlag_display
                ? { value: plats.underlag, label: plats.underlag_display.namn }
                : plats.underlag,
        };
    };

    const onSubmit = async (formData: any) => {
        const plats = isEditing.value ? { ...formData, id: parameters?.value?.id } : formData;
        try {
            if (isEditing.value) {
                await store.dispatch("skreg/platser/updatePlats", { plats });
            } else {
                await store.dispatch("skreg/platser/createPlats", { plats });
            }
            flashMessage(isEditing.value ? "Platsen har uppdaterats" : "Platsen har skapats", 3000);
            store.dispatch("tabs/closeTab", tabId.value);
        } catch (thrownError) {
            const error = thrownError as AxiosError;
            if (error?.response?.data) {
                handleErrorResponse(error);
                return;
            }
            flashWarning(isEditing.value ? "Kunde inte uppdatera platsen." : "Kunde inte spara platsen.");
        }
    };

    const onCancel = ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        if (hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    { title: "Fortsätt redigera", type: "secondary" },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => store.dispatch("tabs/closeTab", tabId.value),
                    },
                ],
            });
            return;
        }
        store.dispatch("tabs/closeTab", tabId.value);
    };
</script>

<template>
    <div class="plats-form">
        <KForm
            ref="formRef"
            id="plats-form"
            :initial-values-getter="initForm"
            :submit-content="submitLabel"
            cancel-content="Avbryt"
            :disableSubmit="!hasUnsavedData"
            :errors="inputErrors"
            :formErrors="formErrors"
            @submit="onSubmit"
            @cancel="onCancel"
        >
            <template #header>
                <KHeader :heading="heading" />
            </template>
            <template #default>
                <div class="form-content">
                    <KText name="namn" label="Namn" validation="required" />
                    <UnderlagAutocomplete label="Underlag" validation="required" />
                </div>
            </template>
        </KForm>
    </div>
</template>

<style lang="scss" scoped></style>
